import { useMemo, useState } from "react";
import { useApi } from "@/hooks/useApi";
import type { Empresa } from "@/@types/Api_Extracted";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/datatable";
import { CommonFeatures, EMPTY_ARRAY } from "@/hooks/useTanstackTable";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { DialogEditEmpresa } from "./EmpresaDialog";
import {
	useTable, ColumnFiltersState,
	tableFeatures, createColumnHelper,
} from "@tanstack/react-table";

export function UserListTable() {
	const api = useApi();
	const query = api.useQuery("get", "/v1/Admin/Empresas", {});

	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const { columns, features } = useMemo(() => Columns(query.refetch), [query.refetch]);
	const table = useTable({
		features,
		columns,
		data: query.data ?? EMPTY_ARRAY,
		state: { columnFilters },
		onColumnFiltersChange: setColumnFilters,
	});

	return (
		<DataTable
			table={table}
			noResultsMessage="Aún no hay imputaciones registradas"
			errorMessage="Error al cargar las imputaciones"
			isLoading={query.isPending}
			isError={query.isError}
		/>
	);
}

function Columns(onSuccess: () => unknown) {
	const columnHelper = createColumnHelper<typeof features, Empresa>();

	const features = tableFeatures(CommonFeatures);

	const columns = columnHelper.columns([
		columnHelper.accessor("idUsuario", {
			header: "ID",
			enableSorting: false,
			enableColumnFilter: false,
			size: -1,
			cell: (props) => props.renderValue(),
		}),
		columnHelper.accessor("razonSocial", {
			header: "Razón Social",
			meta: {
				filterVariant: "text",
			},
			size: -1,
			cell: (props) => props.renderValue(),
		}),
		columnHelper.accessor("username", {
			header: "Email",
			meta: {
				filterVariant: "text",
			},
			size: -1,
			cell: (props) => props.renderValue(),
		}),
		columnHelper.accessor("estado", {
			header: "Estado",
			meta: {
				filterVariant: "select",
				filterOptions: localizedStatus,
			},
			size: -1,
			cell: (props) => localizedStatus[props.getValue()],
		}),
		columnHelper.display({
			id: "acciones",
			header: "Acciones",
			size: -1,
			cell: (props) => (
				<AccionesFila fila={props.row.original} onSuccess={onSuccess} />
			),
		}),
	]);

	return { columns, features };
}

function AccionesFila(props: {
	readonly fila: Empresa;
	readonly onSuccess: () => void;
}) {
	const [open, setOpen] = useState(false);
	const onSuccess = () => {
		setOpen(false);
		props.onSuccess();
	};

	return (
		<div className="grid grid-cols-1 gap-2">
			<Dialog onOpenChange={setOpen} open={open}>
				<DialogTrigger asChild>
					<Button variant="secondary">Editar</Button>
				</DialogTrigger>
				<DialogEditEmpresa empresa={props.fila} onSuccess={onSuccess} />
			</Dialog>
		</div>
	);
}

const localizedStatus = {
	PENDING: "Pendiente",
	ACTIVE: "Activo",
	INACTIVE: "Inactivo",
};
